import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { expensesAPI, venuesAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useOffline } from '../context/OfflineContext';
import usePermissions from '../hooks/usePermissions';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { 
  Plus, Search, Receipt, Edit, Trash2, Filter, 
  Building2, Calendar, CreditCard, Wallet, RefreshCw,
  TrendingUp, TrendingDown, PieChart, BarChart3,
  Zap, Wrench, Users, Package, Megaphone, Home,
  Shield, PenTool as Tool, Utensils, Truck, FileText, MoreHorizontal,
  IndianRupee, Download, ChevronDown, Settings, Tag, Lock
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Wallet },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
  { value: 'upi', label: 'UPI', icon: CreditCard },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'cheque', label: 'Cheque', icon: FileText }
];

const CATEGORY_ICONS = {
  zap: Zap,
  wrench: Wrench,
  users: Users,
  package: Package,
  megaphone: Megaphone,
  home: Home,
  shield: Shield,
  tool: Tool,
  utensils: Utensils,
  truck: Truck,
  'file-text': FileText,
  'more-horizontal': MoreHorizontal,
  receipt: Receipt
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showReports, setShowReports] = useState(false);
  
  // Filters
  const [filterVenue, setFilterVenue] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    venueId: '',
    categoryId: '',
    amount: '',
    description: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    vendorName: '',
    invoiceNumber: '',
    notes: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#6B7280'
  });
  
  const { toast } = useToast();
  const { isOffline, checkOfflineAction } = useOffline();
  const { canViewExpenses, canCreateExpenses, canEditExpenses, canDeleteExpenses, canExportExpenses } = usePermissions();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [expensesData, categoriesData, venuesData, summaryData] = await Promise.all([
        expensesAPI.getAll(),
        expensesAPI.getCategories(),
        venuesAPI.getAll(),
        expensesAPI.getSummary()
      ]);
      setExpenses(expensesData);
      setCategories(categoriesData);
      setVenues(venuesData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load expenses',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      venueId: '',
      categoryId: '',
      amount: '',
      description: '',
      expenseDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      vendorName: '',
      invoiceNumber: '',
      notes: ''
    });
    setEditingExpense(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkOfflineAction(editingExpense ? 'edit' : 'create')) return;
    
    if (!formData.venueId || !formData.categoryId || !formData.amount || !formData.description) {
      toast({
        title: 'Error',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      if (editingExpense) {
        await expensesAPI.update(editingExpense.id, expenseData);
        toast({ title: 'Success', description: 'Expense updated successfully' });
      } else {
        await expensesAPI.create(expenseData);
        toast({ title: 'Success', description: 'Expense added successfully' });
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save expense',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (expense) => {
    if (!checkOfflineAction('edit')) return;
    setEditingExpense(expense);
    setFormData({
      venueId: expense.venueId,
      categoryId: expense.categoryId,
      amount: expense.amount.toString(),
      description: expense.description,
      expenseDate: expense.expenseDate,
      paymentMethod: expense.paymentMethod,
      vendorName: expense.vendorName || '',
      invoiceNumber: expense.invoiceNumber || '',
      notes: expense.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (expense) => {
    if (!checkOfflineAction('delete')) return;
    setExpenseToDelete(expense);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await expensesAPI.delete(expenseToDelete.id);
      toast({ title: 'Deleted', description: 'Expense deleted successfully' });
      setDeleteConfirmOpen(false);
      setExpenseToDelete(null);
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete expense',
        variant: 'destructive'
      });
    }
  };

  // Category management
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) return;
    
    try {
      if (editingCategory) {
        await expensesAPI.updateCategory(editingCategory.id, categoryForm);
        toast({ title: 'Success', description: 'Category updated' });
      } else {
        await expensesAPI.createCategory(categoryForm);
        toast({ title: 'Success', description: 'Category created' });
      }
      setCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '', color: '#6B7280' });
      setEditingCategory(null);
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save category',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? This will fail if expenses use this category.`)) return;
    
    try {
      await expensesAPI.deleteCategory(category.id);
      toast({ title: 'Deleted', description: 'Category deleted' });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Cannot delete category with existing expenses',
        variant: 'destructive'
      });
    }
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => {
    if (filterVenue !== 'all' && exp.venueId !== filterVenue) return false;
    if (filterCategory !== 'all' && exp.categoryId !== filterCategory) return false;
    if (filterPayment !== 'all' && exp.paymentMethod !== filterPayment) return false;
    if (searchTerm && !exp.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !exp.vendorName?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const totalFiltered = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  // Export PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text('Expense Report', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 28);
    doc.text(`Total: ${formatCurrency(totalFiltered)}`, 14, 34);
    
    const tableData = filteredExpenses.map(e => [
      formatDate(e.expenseDate),
      e.venueName,
      e.categoryName,
      e.description.substring(0, 30),
      e.paymentMethod,
      formatCurrency(e.amount)
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [['Date', 'Venue', 'Category', 'Description', 'Payment', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [20, 184, 166] }
    });
    
    doc.save(`Expenses_${new Date().toISOString().split('T')[0]}.pdf`);
    toast({ title: 'Downloaded', description: 'Expense report saved' });
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = CATEGORY_ICONS[iconName] || Receipt;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Check if user has permission to view expenses
  if (!canViewExpenses()) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="p-4 bg-gray-100 dark:bg-secondary rounded-full">
          <Lock className="w-12 h-12 text-gray-400 dark:text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold text-gray-700 dark:text-foreground">Access Restricted</h2>
        <p className="text-gray-500 dark:text-muted-foreground text-center max-w-md">
          You don&apos;t have permission to view expenses. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <Receipt className="w-6 h-6 sm:w-7 sm:h-7 text-teal-600" />
            Expenses
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">Track venue-wise expenses</p>
        </div>
        <div className="flex gap-2">
          {canCreateExpenses() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCategoryDialogOpen(true)}
              className="text-xs sm:text-sm"
            >
              <Tag className="w-4 h-4 mr-1" />
              Categories
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReports(!showReports)}
            className="text-xs sm:text-sm"
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            {showReports ? 'Hide' : 'Reports'}
          </Button>
          {canCreateExpenses() && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-xs sm:text-sm"
                  disabled={isOffline}
                  onClick={() => {
                    if (!checkOfflineAction('create')) return;
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader className="pb-2">
                  <DialogTitle className="text-base sm:text-lg">{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
                  <DialogDescription className="text-xs sm:text-sm">Record a venue expense</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  {/* Venue - Full width on mobile */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Venue *</Label>
                    <Select value={formData.venueId} onValueChange={(v) => setFormData({...formData, venueId: v})}>
                      <SelectTrigger className="h-9 text-xs sm:text-sm w-full">
                        <SelectValue placeholder="Select venue" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        <SelectItem value="collective">
                          <span className="flex items-center gap-2">
                            <Building2 className="w-3 h-3 text-purple-600 flex-shrink-0" />
                            <span className="font-medium text-purple-700 truncate">All Venues (Collective)</span>
                          </span>
                        </SelectItem>
                        <div className="border-t my-1" />
                        {venues.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            <span className="truncate">{v.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Category - Full width on mobile */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Category *</Label>
                    <Select value={formData.categoryId} onValueChange={(v) => setFormData({...formData, categoryId: v})}>
                      <SelectTrigger className="h-9 text-xs sm:text-sm w-full">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                              <span className="truncate">{c.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                
                  {/* Amount & Date - 2 columns */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs sm:text-sm">Amount (₹) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        className="h-9 text-xs sm:text-sm w-full"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs sm:text-sm">Date *</Label>
                      <Input
                        type="date"
                        value={formData.expenseDate}
                        onChange={(e) => setFormData({...formData, expenseDate: e.target.value})}
                        className="h-9 text-xs sm:text-sm w-full"
                      />
                    </div>
                  </div>
                
                  {/* Description - Full width */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Description *</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="h-9 text-xs sm:text-sm w-full"
                      placeholder="What was this expense for?"
                    />
                  </div>
                
                  {/* Payment Method - Full width on mobile */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Payment Method</Label>
                    <Select value={formData.paymentMethod} onValueChange={(v) => setFormData({...formData, paymentMethod: v})}>
                      <SelectTrigger className="h-9 text-xs sm:text-sm w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Vendor Name - Full width on mobile */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Vendor Name</Label>
                    <Input
                      value={formData.vendorName}
                      onChange={(e) => setFormData({...formData, vendorName: e.target.value})}
                      className="h-9 text-xs sm:text-sm w-full"
                      placeholder="Optional"
                    />
                  </div>
                
                  {/* Invoice Number - Full width */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Invoice Number</Label>
                    <Input
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                      className="h-9 text-xs sm:text-sm w-full"
                      placeholder="Optional"
                    />
                  </div>
                
                  {/* Notes - Full width */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="text-xs sm:text-sm min-h-[60px] w-full resize-none"
                      placeholder="Additional notes..."
                    />
                  </div>
                
                  {/* Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 h-9 sm:h-10 text-xs sm:text-sm">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 h-9 sm:h-10 text-xs sm:text-sm bg-teal-600 hover:bg-teal-700">
                      {editingExpense ? 'Update' : 'Add'} Expense
                    </Button>
                  </div>
                </form>
              </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 sm:p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-red-900 truncate">{formatCurrency(summary.totalAmount)}</p>
                  <p className="text-[10px] sm:text-xs text-red-600">Total Expenses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-blue-900">{summary.totalCount}</p>
                  <p className="text-[10px] sm:text-xs text-blue-600">Total Entries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-purple-900 truncate">{formatCurrency(summary.averageExpense)}</p>
                  <p className="text-[10px] sm:text-xs text-purple-600">Avg/Entry</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 sm:p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-teal-900">{categories.length}</p>
                  <p className="text-[10px] sm:text-xs text-teal-600">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports Section */}
      {showReports && summary && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 dark:text-foreground">
              <PieChart className="w-5 h-5 text-indigo-600" />
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* By Category */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-foreground mb-2">By Category</h4>
              <div className="space-y-2">
                {summary.byCategory?.slice(0, 5).map((cat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.categoryColor }} />
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-foreground flex-1 truncate">{cat.categoryName}</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-foreground">{formatCurrency(cat.total)}</span>
                    <span className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground">({cat.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* By Venue */}
            {summary.byVenue?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-foreground mb-2">By Venue</h4>
                <div className="space-y-2">
                  {summary.byVenue?.slice(0, 5).map((v, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-700 dark:text-foreground truncate">{v.venueName}</span>
                      <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-foreground">{formatCurrency(v.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
            <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground">Filters</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Select value={filterVenue} onValueChange={setFilterVenue}>
              <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Venue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                <SelectItem value="collective">
                  <span className="text-purple-700">Collective Only</span>
                </SelectItem>
                <div className="border-t my-1" />
                {venues.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPayment} onValueChange={setFilterPayment}>
              <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                {PAYMENT_METHODS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 sm:h-9 pl-7 text-xs sm:text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses List */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm sm:text-lg dark:text-foreground">Expense Records</CardTitle>
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
              {filteredExpenses.length} entries • Total: {formatCurrency(totalFiltered)}
            </p>
          </div>
          {canExportExpenses() && (
            <Button variant="outline" size="sm" onClick={exportPDF} className="text-xs">
              <Download className="w-3.5 h-3.5 mr-1" />
              PDF
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Receipt className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-muted-foreground text-sm sm:text-base">No expenses found</p>
              <p className="text-gray-400 dark:text-muted-foreground text-xs sm:text-sm">Add your first expense to get started</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 max-h-[500px] overflow-y-auto">
              {filteredExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:bg-secondary rounded-lg transition-colors group"
                  data-testid={`expense-item-${expense.id}`}
                >
                  <div 
                    className="p-1.5 sm:p-2 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: `${expense.categoryColor}20` }}
                  >
                    {React.createElement(getCategoryIcon('receipt'), {
                      className: "w-4 h-4 sm:w-5 sm:h-5",
                      style: { color: expense.categoryColor }
                    })}
                  </div>
                  
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-foreground truncate">{expense.description}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <Badge 
                            className="text-[9px] sm:text-xs px-1.5 py-0"
                            style={{ backgroundColor: `${expense.categoryColor}20`, color: expense.categoryColor }}
                          >
                            {expense.categoryName}
                          </Badge>
                          {expense.venueId === 'collective' ? (
                            <Badge className="text-[9px] sm:text-xs px-1.5 py-0 bg-purple-100 dark:bg-purple-900/30 text-purple-700">
                              All Venues
                            </Badge>
                          ) : (
                            <span className="text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground">{expense.venueName}</span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm sm:text-base font-bold text-red-600 flex-shrink-0">
                        -{formatCurrency(expense.amount)}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center gap-2 text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground">
                        <span className="flex items-center gap-0.5">
                          <Calendar className="w-3 h-3" />
                          {formatDate(expense.expenseDate)}
                        </span>
                        <span className="capitalize">{expense.paymentMethod.replace('_', ' ')}</span>
                        {expense.vendorName && <span>• {expense.vendorName}</span>}
                      </div>
                      
                      <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        {canEditExpenses() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(expense)}
                            className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:bg-blue-900/20"
                          >
                            <Edit className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </Button>
                        )}
                        {canDeleteExpenses() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(expense)}
                            className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/20"
                          >
                            <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
              Expense Categories
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add/Edit Category Form */}
            <form onSubmit={handleCategorySubmit} className="space-y-3 p-3 bg-gray-50 dark:bg-secondary rounded-lg">
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                    className="h-8 text-xs w-full"
                    placeholder="Category name"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                      className="h-8 text-xs w-full"
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Color</Label>
                    <Input
                      type="color"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                      className="h-8 w-12 p-1"
                    />
                  </div>
                </div>
              </div>
              <Button type="submit" size="sm" className="w-full bg-teal-600 hover:bg-teal-700 h-8 text-xs">
                {editingCategory ? 'Update' : 'Add'} Category
              </Button>
            </form>
            
            {/* Existing Categories */}
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between p-2 bg-white dark:bg-card border rounded-lg gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-foreground truncate">{cat.name}</p>
                      {cat.description && (
                        <p className="text-xs text-gray-500 dark:text-muted-foreground truncate">{cat.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingCategory(cat);
                        setCategoryForm({ name: cat.name, description: cat.description || '', color: cat.color });
                      }}
                      className="h-7 w-7 p-0 text-blue-600"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    {!cat.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(cat)}
                        className="h-7 w-7 p-0 text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Expense"
        description={`Are you sure you want to delete this expense of ${expenseToDelete ? formatCurrency(expenseToDelete.amount) : ''}?`}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default Expenses;
